import { useParams } from 'react-router-dom';
import { RenderIf } from 'react-rainbow-components';
import LoadingShape from 'components/LoadingShape';
import useOpportunity from 'data/firestore/agent/opportunity/use';
import usePipeline from 'data/firestore/agent/pipeline/use';
import usePipelineStage from 'data/firestore/agent/pipeline/stage/use';
import { formatRelativeDate } from 'data/services/date/formatter';
import priceFormatter from 'data/services/price/formatter';
import OpportunityStuckBadge from 'components/OpportunityStuckBadge';
import getDisplayName from 'data/services/profile/getDisplayName';
import getVariantFromScore from 'pages/Opportunities/helpers/getVariantFromScore';
import getInitials from 'data/services/string/getInitials';
import {
    CardActions,
    CardBody,
    CardContainer,
    CardFooter,
    CardHeader,
    CardHeaderContent,
    CardTitle,
    LastUpdateDateContainer,
    StyledAlertCircle,
    StyledClockIcon,
    Score,
    PriceLabel,
    OwnerAvatar,
    StyledListTile,
    StyledRecordField,
    PipelineInfoContainer,
} from './styled';

const OpportunityInfo = ({ opportunityId }: { opportunityId: string }) => {
    const { agentId = '' } = useParams();
    const { data: opportunity, isLoading: isLoadingOpportunity } = useOpportunity(
        agentId,
        opportunityId,
        {
            disabled: !agentId || !opportunityId,
            track: [opportunityId],
        },
    );

    const { data: pipeline, isLoading: isLoadingPipeline } = usePipeline(
        agentId,
        opportunity?.pipelineId as string,
        {
            disabled: !agentId || !opportunity?.pipelineId,
            track: [opportunity?.pipelineId],
        },
    );

    const { data: pipelineStage, isLoading: isLoadingPipelineStage } = usePipelineStage(
        agentId,
        opportunity?.pipelineId as string,
        opportunity?.stageId as string,
        {
            disabled: !agentId || !opportunity?.pipelineId || !opportunity?.stageId,
            track: [opportunity?.stageId],
        },
    );

    if (
        isLoadingOpportunity || isLoadingPipeline || isLoadingPipelineStage
    ) return <LoadingShape />;

    const amount = priceFormatter('usd').format((opportunity?.amount || 0) / 100);

    const relativeDateValue = opportunity?.updatedAt && `Updated ${formatRelativeDate(opportunity.updatedAt)}`;

    const isStuck = opportunity?.stuck?.condition === 'stalled';

    const ownerDisplayName = opportunity?.owner && getDisplayName(opportunity?.owner);

    const ownerInitials = (ownerDisplayName && ownerDisplayName !== 'Unknown') ? getInitials(ownerDisplayName) : null;

    return (
        <CardContainer>
            <CardHeader>
                <CardHeaderContent>
                    <CardTitle target="_blank" to={`/${agentId}/opportunities/${opportunity?.pipelineId}/view/board?showOpportunity=${opportunityId}`}>
                        {opportunity?.name}
                    </CardTitle>
                </CardHeaderContent>
            </CardHeader>
            <CardBody>
                <PipelineInfoContainer>
                    <StyledRecordField label="Pipeline" value={pipeline?.name} />
                    <StyledRecordField label="Stage" value={pipelineStage?.name} />
                </PipelineInfoContainer>
                <LastUpdateDateContainer>
                    <RenderIf isTrue={!isStuck}>
                        <StyledClockIcon />
                    </RenderIf>
                    <RenderIf isTrue={isStuck}>
                        <StyledAlertCircle />
                    </RenderIf>
                    {relativeDateValue}
                    <OpportunityStuckBadge stuck={opportunity?.stuck} />
                </LastUpdateDateContainer>
                <RenderIf isTrue={!!opportunity?.owner}>
                    <StyledListTile
                        icon={(
                            <OwnerAvatar
                                size="x-small"
                                initials={ownerInitials}
                                src={opportunity?.owner?.photoURL}
                                value={opportunity?.owner?.uid}
                            />
                        )}
                        title={`${ownerDisplayName} (Owner)`}
                    />
                </RenderIf>
            </CardBody>
            <CardFooter>
                <PriceLabel>{amount}</PriceLabel>
                <CardActions>
                    <Score
                        variant={getVariantFromScore(opportunity?.score)}
                        value={opportunity?.score || 0}
                    />
                </CardActions>
            </CardFooter>
        </CardContainer>
    );
};

export default OpportunityInfo;
